import {Box, Loading, Typography} from '@viptech/react-components'
import moment from 'moment'
import {useSnackbar} from 'notistack'
import {useEffect, useId, useState} from 'react'
import {useCompany} from 'src/contexts/CompanyContext'
import useDashboardDetectionsReport from 'src/hooks/api/dashboard/useDashboardDetectionsReport'
import useDashboardEventsReport from 'src/hooks/api/dashboard/useDashboardEventsReport'
import useExportFacialDetectionsAggreatedByHours from 'src/hooks/api/personalized-dashboard/useExportAggregatedFacialData'
import useExportFacialDetectionsCountingAggregated from 'src/hooks/api/personalized-dashboard/useExportFacialDetectionsCountingAggregated'
import usePersonalizedThemeByCompany from 'src/hooks/usePersonalizedThemeByCompany'
import {fileDateFormat} from 'src/utilities/datetimeUtils'
import styled from 'styled-components'

type Props = {
  start: string
  end: string
  filter: {status?: Array<string>; cameraIds: Array<number>; clientIds: Array<number>}
  dashboardSelected: 'events' | 'detections' | 'analytic' | 'synthetic'
}

export default function ExportDataButton({dashboardSelected, start, end, filter}: Props) {
  const {selectedCompanyId} = useCompany()
  const {styledTheme} = usePersonalizedThemeByCompany()

  const [buttonLoading, setButtonLoading] = useState<Array<'csv' | 'pdf'>>([])

  const [isOpen, setIsOpen] = useState(false)
  const dropdownId = useId()
  const buttonId = useId()
  const screenBottomMargin = 20
  const {enqueueSnackbar} = useSnackbar()

  const {mutateAsync: mutateDetectionsAsync, isPending: isPendingDetections} = useDashboardDetectionsReport()
  const {mutateAsync: mutateEventsAsync, isPending: isPendingEvents} = useDashboardEventsReport()

  const {mutateAsync: mutateAggregatedByHoursAsync, isPending: isPendingAggregatedByHours} = useExportFacialDetectionsAggreatedByHours()
  const {mutateAsync: mutateAggregatedCounting, isPending: isPendingAggregatedCounting} = useExportFacialDetectionsCountingAggregated()

  const isLoading = isPendingDetections || isPendingEvents || isPendingAggregatedByHours || isPendingAggregatedCounting

  const handlePersonalizedDash = async (fileType: 'csv' | 'pdf') => {
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
      const offset = new Date().getTimezoneOffset() / 60

      if (dashboardSelected === 'analytic' && fileType === 'csv') {
        const {data: dataByHours} = await mutateAggregatedByHoursAsync({
          start,
          end,
          fileType,
          filter: {...filter, companyIds: [selectedCompanyId]},
          timeZone,
          offset,
        })
        const type = 'text/csv'
        const blobByHours = new Blob([dataByHours], {type})
        const urlByHours = window.URL.createObjectURL(blobByHours)
        const linkByHours = document.createElement('a')
        linkByHours.href = urlByHours
        linkByHours.download = `relatório_agregado_por_horas_${start}_${end}.${fileType}`
        linkByHours.click()

        const {data} = await mutateAggregatedCounting({start, end, fileType, filter: {...filter, companyIds: [selectedCompanyId]}})
        const blob = new Blob([data], {type})
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `relatório_agregado_${start}_${end}.${fileType}`
        a.click()
      }
      if (dashboardSelected === 'analytic' && fileType === 'pdf') {
        const type = 'application/pdf'
        const {data} = await mutateAggregatedCounting({start, end, fileType, filter: {...filter, companyIds: [selectedCompanyId]}, timeZone, offset})
        const blob = new Blob([data], {type})
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `relatório_agregado_${start}_${end}.${fileType}`
        a.click()
      }
    } catch (error) {
      enqueueSnackbar(`Falha ao baixar arquivo ${fileType}`, {variant: 'error'})
    } finally {
      setButtonLoading((old) => old.filter((item) => item !== fileType))
      setIsOpen(false)
    }
  }

  const downloadFile = async (fileType: 'csv' | 'pdf') => {
    try {
      setButtonLoading((old) => [...old, fileType])
      if (dashboardSelected === 'analytic' || dashboardSelected === 'synthetic') {
        handlePersonalizedDash(fileType)
        return
      }

      const mutation = dashboardSelected === 'events' ? mutateEventsAsync : mutateDetectionsAsync
      const type = fileType === 'csv' ? 'text/csv' : 'application/pdf'
      const fileName = dashboardSelected === 'events' ? 'relatório-eventos' : 'relatório-detecções'
      const startDate = fileDateFormat(moment(new Date(start)).format())
      const finalDate = fileDateFormat(moment(new Date(end)).format())
      const {data} = await mutation({start, end, fileType, filter: {...filter, companyIds: [selectedCompanyId]}})
      const blob = new Blob([data], {type})
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${fileName}_${startDate}_${finalDate}.${fileType}`
      a.click()
    } catch (error) {
      enqueueSnackbar(`Falha ao baixar arquivo ${fileType}`, {variant: 'error'})
    } finally {
      setButtonLoading((old) => old.filter((item) => item !== fileType))
      setIsOpen(false)
    }
  }

  useEffect(() => {
    const listElement = document.getElementById(buttonId)
    const inputElement = document.getElementById(dropdownId)

    if (!inputElement || !listElement) return
    const {height: inputElementHeight} = inputElement.getBoundingClientRect()
    const {bottom: listElementBottom} = listElement.getBoundingClientRect()

    if (listElementBottom > window.screen.height - screenBottomMargin) listElement.style.bottom = `${inputElementHeight + 8}px`
  }, [dropdownId, isOpen, buttonId])

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      const isInputContainerChild = target.parentElement?.id === dropdownId
      const isDropdownButtonChild = target.parentElement?.id === buttonId
      if (isInputContainerChild || isDropdownButtonChild) return
      if (isOpen) setIsOpen(false)
    }

    window.addEventListener('click', handleOutsideClick)
    return () => window.removeEventListener('click', handleOutsideClick)
  }, [dropdownId, buttonId])

  return (
    <div>
      <Box
        id={buttonId}
        onClick={() => setIsOpen((old) => !old)}
        height='40px'
        fontSize='14px'
        backgroundColor={styledTheme.colors.primary}
        display='flex'
        align='center'
        padding='0 16px'
        maxWidth='250px'
        justifyContent='center'
        style={{cursor: 'pointer'}}
        borderRadius='8px'>
        <Typography variant='span' color='white' size='12px' cursor='pointer'>
          Exportar dados
        </Typography>
      </Box>

      {isOpen && (
        <Box
          boxShadow='rgba(0, 0, 0, 0.1) 0px 4px 12px'
          id={dropdownId}
          backgroundColor='white'
          position='absolute'
          width='152px'
          marginTop='8px'
          display='flex'
          direction='column'
          padding='16px 0px'
          borderRadius='8px'
          rowGap='4px'
          style={{zIndex: 4}}>
          <Item
            variant='label'
            cursor='pointer'
            color='#344054'
            onClick={() => !isLoading && downloadFile('pdf')}
            backgroundColor={isLoading ? '#f5f5f5' : undefined}>
            {isLoading && buttonLoading.find((i) => i === 'pdf') && (
              <Box position='absolute' left='8px' top='10px'>
                <Loading size='12px' />
              </Box>
            )}
            Salvar em PDF
          </Item>
          <Item
            variant='label'
            cursor='pointer'
            color='#344054'
            onClick={() => !isLoading && downloadFile('csv')}
            backgroundColor={isLoading ? '#f5f5f5' : undefined}>
            {isLoading && buttonLoading.find((i) => i === 'csv') && (
              <Box position='absolute' left='8px' top='10px'>
                <Loading size='12px' />
              </Box>
            )}
            Salvar em CSV
          </Item>
        </Box>
      )}
    </div>
  )
}

const Item = styled(Typography)<{backgroundColor?: string}>`
  padding: 8px 4px;
  display: flex;
  gap: 4px;
  position: relative;
  margin-left: 4px;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.backgroundColor ?? 'white'};
  :hover {
    background-color: #f5f5f5;
  }
`
